import { FC, useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'

import {
  FollowedArtistsQuery,
  FollowArtistMutation,
  FollowArtistMutationVariables,
  UnfollowArtistMutation,
  UnfollowArtistMutationVariables,
  UserRoleNameEnum,
} from 'gql'
import { STAR } from 'routes/paths'
import { useDataLayer } from '~hooks'
import { notifySuccess } from '~shared'
import { USER_FOLLOWED_ARTISTS } from 'gql/user/queries'
import { FOLLOW_ARTIST, UNFOLLOW_ARTIST } from 'gql/artist/mutations'
import { FollowProps } from '../types'
import FollowButton from '../button'
import ModalSignUp from '../signup'

// NOTE: currently I've implemented fetch follows logic in one button.
// But if later we want to add follow button on lists we might want to use context.

const FollowArtist: FC<FollowProps> = ({ id, slug, starName }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const dl = useDataLayer()

  const { data, loading } = useQuery<FollowedArtistsQuery>(USER_FOLLOWED_ARTISTS)

  const [followArtist, { loading: followLoading }] = useMutation<
    FollowArtistMutation,
    FollowArtistMutationVariables
  >(FOLLOW_ARTIST, {
    refetchQueries: ['FollowedArtists'],
  })

  const [unfollowArtist, { loading: unfollowLoading }] = useMutation<
    UnfollowArtistMutation,
    UnfollowArtistMutationVariables
  >(UNFOLLOW_ARTIST, {
    refetchQueries: ['FollowedArtists'],
  })

  const isFollowed = useMemo(() => {
    if (!data?.currentUser) {
      return false
    }

    return !!data?.currentUser?.followedArtists.edges.find(
      ({ node: { id: followedId } }) => followedId === id
    )
  }, [data, id])

  const onFollowChange = useCallback(() => {
    if (data?.currentUser && data?.currentUser?.type?.name !== UserRoleNameEnum.Guest) {
      if (isFollowed) {
        dl.push({ event: 'unfollow_star_venue' })
        unfollowArtist({
          variables: {
            id,
          },
        })
      } else {
        dl.push({ event: 'follow_star_venue' })
        followArtist({
          variables: {
            id,
          },
        })
      }
    } else {
      setIsModalOpen(true)
    }
  }, [data, followArtist, id, isFollowed, slug, unfollowArtist])

  const canFollowStar =
    id && data?.currentUser && data?.currentUser?.type?.name !== UserRoleNameEnum.Guest

  const follow = () => {
    dl.push({ event: 'follow_star_venue' })
    followArtist({
      variables: {
        id,
      },
    })
    setIsModalOpen(false)
    notifySuccess(`You're following the artist.`)
  }

  return (
    <>
      <FollowButton
        onClick={onFollowChange}
        isFollowed={isFollowed}
        disabled={loading || followLoading || unfollowLoading}
      />
      <ModalSignUp
        follow={follow}
        starName={starName}
        isOpen={isModalOpen}
        canFollow={canFollowStar}
        setIsOpen={setIsModalOpen}
        redirectUrl={STAR(slug)}
      />
    </>
  )
}

export default FollowArtist
